<script setup lang="ts">
import { X as XIcon } from '@lucide/vue'
import { ref, watchEffect } from 'vue'

import BaseSearchBar from '@/components/ui/BaseSearchBar.vue'
import type { useSmartSearch } from '@/composables/useSmartSearch'
import { safeAreaTopOffset } from '@/services/safeAreaService'
import { Z_INDEX } from '@/utils/zIndex'

defineOptions({ name: 'MobileSearchInputBar' })

// MapOut-style pinned search field at the TOP of the mobile screen, kept separate from the
// results drawer so the map (and result pins) stay visible while browsing. MapHeader does not
// render on mobile, so the top region is free.
const props = defineProps<{ search: ReturnType<typeof useSmartSearch> }>()
const emit = defineEmits<{ (e: 'focus'): void; (e: 'blur'): void }>()

const { query, inputRef, handleInputKeydown, closeSearch } = props.search
// Standard top inset below the notch / Dynamic Island (shared 12px gap).
const topOffset = safeAreaTopOffset()

// Forward the shared BaseSearchBar's <input> to the composable's inputRef (focus/select).
const searchBarRef = ref<{ inputEl: HTMLInputElement | null } | null>(null)
watchEffect(() => {
  inputRef.value = searchBarRef.value?.inputEl ?? null
})
</script>

<template>
  <div
    class="pointer-events-auto fixed left-3 right-3 top-0"
    :style="{ paddingTop: topOffset, zIndex: String(Z_INDEX.MOBILE_TAB_BAR) }"
    data-testid="mobile-search-input-bar"
  >
    <div class="flex items-center gap-1.5">
      <BaseSearchBar
        ref="searchBarRef"
        v-model="query"
        size="lg"
        class="min-w-0 flex-1"
        data-testid="mobile-search-input"
        placeholder="Search places, collections, activities, and routes..."
        aria-label="Search places, collections, activities, and routes"
        enterkeyhint="search"
        autocomplete="off"
        spellcheck="false"
        @keydown="handleInputKeydown"
        @focus="emit('focus')"
        @blur="emit('blur')"
      />
      <!-- Dismiss: a filled circular "X" that closes search and returns to the map — the Apple
           Maps affordance for floating (non-modal) search, in place of a "Cancel" text button. -->
      <button
        type="button"
        class="flex h-11 w-11 shrink-0 items-center justify-center hover:cursor-pointer"
        aria-label="Close search"
        @click="closeSearch"
      >
        <span
          class="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200"
        >
          <XIcon class="h-5 w-5" />
        </span>
      </button>
    </div>
  </div>
</template>
